/**
 * AgentDropdown Component
 * 
 * A dropdown selector for agents with:
 * - Selection of active agent
 * - Inline editing of agent name (pencil icon)
 * - Visual feedback for selected agent
 * 
 * Uses portal for dropdown to avoid z-index issues
 */

import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as DownArrowSVG } from '../../assets/images/down-arrow.svg';
import { Agent } from '@back4app2/sdk';
import LoadingSpinner from '../LoadingSpinner';

interface AgentDropdownProps {
  /** List of available agents */
  agents: Agent[];
  /** Currently selected agent ID */
  selectedAgentId: string;
  /** Callback when an agent is selected */
  onSelect: (agentId: string) => void;
  /** Optional callback for updating agent name */
  onUpdate?: (agentId: string, name: string) => Promise<void>;
}

/**
 * Dropdown component for selecting and editing agents
 * Supports inline name editing when onUpdate callback is provided
 */
const AgentDropdown = ({ agents, selectedAgentId, onSelect, onUpdate }: AgentDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Editing state
  const [editingAgentId, setEditingAgentId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);

  const isDisabled = agents.length === 0;
  const selectedAgent = agents.find(a => a.id === selectedAgentId);

  const updatePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    // Using position: fixed (portal), so viewport coords are correct (no scroll offsets)
    setPosition({
      top: rect.bottom + 8, // 8px = mt-2
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // Calculate position before paint to avoid flashing at (0,0)
  useLayoutEffect(() => {
    if (!isOpen || isDisabled) {
      setPosition(null);
      return;
    }
    updatePosition();
  }, [isOpen, isDisabled, updatePosition]);

  // If layout shifts right after opening (fonts/data), re-measure once on next frame
  useEffect(() => {
    if (!isOpen || isDisabled) return;
    const raf = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(raf);
  }, [isOpen, isDisabled, updatePosition]);

  // If agents become empty while open, close dropdown to avoid weird states
  useEffect(() => {
    if (isDisabled && isOpen) {
      setIsOpen(false);
      setEditingAgentId(null);
    }
  }, [isDisabled, isOpen]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingAgentId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingAgentId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current && 
        dropdownRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setEditingAgentId(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Also close on scroll to prevent misalignment
      const handleScroll = (event: Event) => {
        // Allow scrolling inside the dropdown list without closing it
        const targetNode = event.target as Node | null;
        if (targetNode && dropdownRef.current?.contains(targetNode)) {
          return;
        }
        setIsOpen(false);
        setEditingAgentId(null);
      };
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen]);

  /**
   * Handles selection of an agent from the dropdown
   */
  const handleSelect = (agentId: string) => {
    if (editingAgentId) return; // Don't select while editing
    onSelect(agentId);
    setIsOpen(false);
  };

  /**
   * Starts editing mode for an agent
   */
  const handleStartEdit = useCallback((e: React.MouseEvent, agent: Agent) => {
    e.stopPropagation();
    setEditingAgentId(agent.id);
    setEditingName(agent.name);
  }, []);

  /**
   * Cancels the current editing operation
   */
  const handleCancelEdit = useCallback(() => {
    setEditingAgentId(null);
    setEditingName('');
  }, []);

  /**
   * Saves the edited name
   */
  const handleSaveEdit = useCallback(async () => {
    if (!editingAgentId || !onUpdate || !editingName.trim()) {
      handleCancelEdit();
      return;
    }

    // Don't save if name hasn't changed
    const currentAgent = agents.find(a => a.id === editingAgentId);
    if (currentAgent && currentAgent.name === editingName.trim()) {
      handleCancelEdit();
      return;
    }

    setIsUpdating(true);
    try {
      await onUpdate(editingAgentId, editingName.trim());
      handleCancelEdit();
    } catch (error) {
      console.error('Error updating agent name:', error);
      // Keep edit mode open on error so user can retry
    } finally {
      setIsUpdating(false);
    }
  }, [editingAgentId, editingName, onUpdate, agents, handleCancelEdit]);

  /**
   * Handles keyboard events in the edit input
   */
  const handleEditKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  }, [handleSaveEdit, handleCancelEdit]);

  return (
    <>
      <div className="relative inline-flex items-center">
        {/* Selected value button */}
        <button
          ref={buttonRef}
          className={`appearance-none glass-select text-white text-sm h-10 pl-4 pr-10 transition-all duration-200 flex items-center justify-between min-w-[120px] max-w-[180px] ${
            isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
          }`}
          onClick={() => !isDisabled && setIsOpen(!isOpen)}
          disabled={isDisabled}
          type="button"
          title={isDisabled ? 'No agents available' : (selectedAgent?.name || 'Select Agent')}
        >
          <span className="truncate block max-w-[140px]">{selectedAgent?.name || 'Select Agent'}</span>
          <DownArrowSVG 
            className={`absolute right-3 top-1/2 -translate-y-1/2 text-light-blue opacity-70 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
            width="12" 
            height="12" 
          />
        </button>
      </div>

      {/* Dropdown list rendered via Portal */}
      {isOpen && position && createPortal(
        <div
          ref={dropdownRef}
          className="fixed glass-surface rounded-xl overflow-hidden z-[9999] shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${Math.max(position.width, 240)}px`,
            minWidth: '240px',
          }}
        >
          <div className="max-h-[300px] overflow-y-auto glass-scroll">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className={`w-full text-left px-4 py-3 text-sm transition-colors duration-150 ${
                  agent.id === selectedAgentId
                    ? 'bg-[#208AEC]/20 text-white font-medium'
                    : 'text-white/90 hover:bg-white/5'
                }`}
              >
                {/* Edit mode */}
                {editingAgentId === agent.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={handleEditKeyDown}
                      className="flex-1 bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-[#208AEC]"
                      placeholder="Agent name"
                      disabled={isUpdating}
                      maxLength={128}
                    />
                    {isUpdating ? (
                      <LoadingSpinner width="16px" height="16px" />
                    ) : (
                      <>
                        {/* Save button */}
                        <button
                          onClick={handleSaveEdit}
                          className="p-1 text-emerald-400 hover:text-emerald-300 transition-colors"
                          title="Save (Enter)"
                          type="button"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 6L9 17L4 12" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        {/* Cancel button */}
                        <button
                          onClick={handleCancelEdit}
                          className="p-1 text-red-400 hover:text-red-300 transition-colors"
                          title="Cancel (Esc)"
                          type="button"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  /* Normal display mode */
                  <button
                    className="w-full flex items-center justify-between gap-2 group"
                    onClick={() => handleSelect(agent.id)}
                    type="button"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {agent.id === selectedAgentId && (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-[#208AEC] flex-shrink-0">
                          <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                      <span className="truncate">{agent.name}</span>
                    </div>
                    
                    {/* Edit button - only show if onUpdate is provided */}
                    {onUpdate && (
                      <button
                        onClick={(e) => handleStartEdit(e, agent)}
                        className="p-1.5 text-white/40 hover:text-white hover:bg-white/10 rounded transition-all opacity-0 group-hover:opacity-100"
                        title="Edit name"
                        type="button"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default AgentDropdown;
